/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * Input Range Slider component
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.21.0
 * @since 1.10.0
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { FC, MouseEvent as ReactMouseEventType } from 'react';

import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';

import { icons } from '@assets/index';

import { ImgStyled } from '../Block/Img';
import ArrowTooltip from '../Tooltip/Tooltip';
import { InputRangeSliderSlotStyled, InputRangeSliderStyled } from './styled/InputRangesliderStyled';

interface IInputRangeSliderProps {
    onChange: (value: number) => void;
    onEnd?: (value: number) => void;
    showTooltip?: boolean;
    slots: number;
    tooltipMessage?: string;
    value: number;
}

const InputRangeSlider: FC<IInputRangeSliderProps> = (
    { onChange, onEnd, showTooltip, slots, tooltipMessage, value }) => {

    const SLIDER_THUMB_WIDTH = 44;

    const [slot, setSlot] = useState(value);
    const [position, setPosition] = useState(0);
    // tooltip state
    const [disableHoverListener, setDisableHoverListener] = useState(true);

    const tooltipContentRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const rangeWidth = useRef(0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const containerWidth = useMemo(() => containerRef.current?.clientWidth ?? 0, [containerRef.current?.clientWidth]);

    const slotWidth = useMemo(() => {
        return (sliderRef.current?.clientWidth ?? 0) / (slots + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sliderRef.current?.clientWidth, slots]);

    const draggBounds = useMemo(() => {
        return {
            right: containerWidth - slotWidth - SLIDER_THUMB_WIDTH / 2,
            left: slotWidth / 2 - SLIDER_THUMB_WIDTH / 2,
        }
    }, [containerWidth, slotWidth]);

    const setSliderPosition = useCallback((newSlot: number) => {
        if (rangeWidth.current) {
            setPosition((rangeWidth.current / slots) * newSlot + (slotWidth - SLIDER_THUMB_WIDTH) / 2 );
        }
    }, [slotWidth, slots]);

    useLayoutEffect(() => {
        const containerWidth = containerRef.current?.clientWidth;
        if (!!containerWidth) {
            const rangeWidth = containerWidth - (containerWidth / (slots *  2 + 1));
            const slotWidth = (sliderRef.current?.clientWidth ?? 0) / (slots + 1);
            setPosition((rangeWidth / slots) * slot + (slotWidth - SLIDER_THUMB_WIDTH) / 2 );
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef.current?.clientWidth]);

    useEffect(() => {
        rangeWidth.current = containerWidth - (containerWidth / (slots *  2 + 1));
    }, [containerWidth, slots])

    useEffect(() => {
        if (showTooltip) {
            setTimeout(() => {
                setDisableHoverListener(false);
                tooltipContentRef.current?.dispatchEvent(new MouseEvent('mouseover', {
                    view: window,
                    bubbles: true,
                    cancelable: true,
                }));
            }, 1000)
        } else {
            setTimeout(() => {
                setDisableHoverListener(false);
                tooltipContentRef.current?.dispatchEvent(new MouseEvent('mouseout', {
                    view: window,
                    bubbles: true,
                    cancelable: true,
                }));
                setDisableHoverListener(true);
            }, 1000)
        }
    }, [showTooltip])

    /*
    * -------------------------------------------
    *               Handlers
    * -------------------------------------------
    */

    const onDragEnd = useCallback(() => {
        setSliderPosition(slot);
        onEnd?.(slot);
    }, [onEnd, setSliderPosition, slot]);

    const onSlotClick = useCallback((e: ReactMouseEventType<HTMLDivElement>) => {
        if (e.target instanceof HTMLDivElement) {
            const xCoordinate = e.nativeEvent.offsetX + e.target.offsetLeft;
            let cursorSlotPosition = Math.floor(xCoordinate / (rangeWidth.current / slots));
            cursorSlotPosition = isFinite(cursorSlotPosition) ? cursorSlotPosition : 0;
            let newValue = cursorSlotPosition < slots ? cursorSlotPosition : slots - 1;
            newValue = newValue > slots - 1 ? slots - 1: newValue;
            setSlot(newValue)
            setSliderPosition(newValue);
            onChange(newValue)
            onEnd?.(newValue);
        }
    }, [onChange, onEnd, rangeWidth, setSlot, setSliderPosition, slots]);

    const onDrag = useCallback((e: DraggableEvent, data: DraggableData) => {
        if (rangeWidth.current) {
            let newValue = Math.round(data.x / (rangeWidth.current / slots));
            newValue = newValue > slots - 1  ? slots - 1 : newValue;
            setSlot(newValue)
            onChange(newValue)
        }
    }, [onChange, rangeWidth, setSlot, slots]);

    /*
    * -------------------------------------------
    *           Render component
    * -------------------------------------------
    */

    const renderHalfSlot = useCallback((flexGrow = 'initial', width = 'auto') => (
        <InputRangeSliderSlotStyled
            data-slot={0}
            isBefore={true}
            style={{
                width,
                flexGrow,
            }}
            onClick={onSlotClick}
        />
    ), [onSlotClick]);

    return (
        <InputRangeSliderStyled ref={sliderRef}>
            { renderHalfSlot(1, (sliderRef.current?.clientWidth ?? 0) / (slots * 2 + 1) ) }
            <div className="slider__draggable" ref={containerRef} style={{ flexGrow: slots * 2 + 1 }}>
                { renderHalfSlot(1) }
                {
                    Array.from(Array(slots).keys()).map((s: number) => (
                        <InputRangeSliderSlotStyled
                            data-slot={s}
                            isBefore={s < slot}
                            key={s}
                            style={{ flexGrow: 2 }}
                            onClick={onSlotClick}
                        >
                            <span className="slider__scale-mark" data-slot={s} />
                        </InputRangeSliderSlotStyled>
                    ))
                }
                {
                    <Draggable
                        axis="x"
                        bounds={{
                            top: 0,
                            bottom: 0,
                            left: draggBounds.left,
                            right: draggBounds.right,
                        }}
                        position={{ x: position, y: 0 }}
                        onDrag={onDrag}
                        onStop={onDragEnd}
                    >
                        <div className="slider-thumb">
                            <ArrowTooltip
                                closeTooltipOnDisableChange={false}
                                disableHoverListener={disableHoverListener}
                                message={tooltipMessage}
                                placement="bottom"
                            >
                                <ImgStyled height="28px" ref={tooltipContentRef} src={icons.rangeSliderIcon} width="100%"/>
                            </ArrowTooltip>
                        </div>
                    </Draggable>
                }
            </div>

        </InputRangeSliderStyled>
    );
};

export default InputRangeSlider;
